import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useJobs } from "@/hooks/useJobs";
import { usePipelines } from "@/hooks/usePipeline";
import { useProfile } from "@/hooks/useApi";
import { computePipelineAnalytics, getPipelineForJob } from "@/lib/pipeline-analytics";
import { isInboxJob } from "@/lib/job-ui";
import { buildExploreQuickStarts, buildExploreSearchPath } from "@/lib/explore-defaults";
import { scoreTone } from "@/format";
import { cn } from "@/cn";
import { useMemo } from "react";
import { CareerCommandHome } from "@/components/career-moves";

const TONE_CLASS: Record<string, string> = {
  good: "text-emerald-400",
  warn: "text-amber-400",
  bad: "text-red-400",
  muted: "text-muted-foreground",
};

export function HomePage() {
  return <CareerCommandHome OperationalHomeFallback={OperationalHomeFallback} />;
}

/** Pipeline-stats dashboard shown when CAREER_COMMAND_HOME_ENABLED is off. */
function OperationalHomeFallback() {
  const { user, signOut } = useAuth();
  const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useJobs();
  const { data: pipelinesData, isLoading: pipelinesLoading } = usePipelines();
  const { data: profile } = useProfile();
  const exploreSearchPath = useMemo(
    () => buildExploreSearchPath(profile),
    [profile],
  );
  const exploreSearchLabel = useMemo(() => {
    const [first] = buildExploreQuickStarts(profile);
    return first.length > 42 ? `${first.slice(0, 40)}…` : first;
  }, [profile]);

  const jobs = jobsData?.jobs ?? [];
  const pipelines = pipelinesData?.pipelines ?? [];

  const isLoading = jobsLoading || pipelinesLoading;

  const inboxJobs = useMemo(() => jobs.filter(isInboxJob), [jobs]);

  const stats = useMemo(
    () => computePipelineAnalytics(jobs, pipelines),
    [jobs, pipelines],
  );
  const maxBucket = Math.max(...stats.scoreBuckets, 1);

  const recentJobs = useMemo(
    () =>
      [...jobs]
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )
        .slice(0, 5),
    [jobs],
  );

  return (
    <>
      <header className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Career Pipeline Dashboard
            </h1>
            <p className="mt-1 text-muted-foreground">
              Welcome back{user?.name ? `, ${user.name}` : ""}. Here&rsquo;s
              your job search at a glance.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/profile"
              className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/20"
            >
              Settings
            </Link>
            <button
              onClick={signOut}
              className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/20"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
            <p className="text-sm text-muted-foreground">Loading dashboard data…</p>
          </div>
        </div>
      )}

      {jobsError && !isLoading && (
        <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load data. Please try again later.
        </div>
      )}

      {!isLoading && !jobsError && (
        <>
          <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard label="Total Jobs" value={String(stats.inPipeline)} />
            <StatCard label="Active Pipeline" value={String(stats.active)} />
            <StatCard label="Interviews" value={String(stats.interviews)} />
            <StatCard label="Offers" value={String(stats.offers)} />
            <StatCard
              label="Unprocessed"
              value={String(stats.unprocessed)}
              highlight={stats.unprocessed > 0}
            />
          </div>

          <div className="mb-8 grid gap-6 lg:grid-cols-3">
            <div className="rounded-lg border border-border bg-card p-5">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Quick Actions
              </h2>
              <div className="flex flex-col gap-2">
                <Link
                  to="/ai-assistant"
                  className="rounded-md bg-brand/10 px-3 py-2 text-sm font-medium text-brand transition hover:bg-brand/20"
                >
                  Ask AI
                </Link>
                <Link
                  to="/jobs/add"
                  className="rounded-md bg-muted/10 px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted/20"
                >
                  Add a Job
                </Link>
                <Link
                  to="/explore"
                  className="rounded-md bg-brand/10 px-3 py-2 text-sm font-medium text-brand transition hover:bg-brand/20"
                >
                  Find Jobs
                </Link>
                <Link
                  to={exploreSearchPath}
                  className="rounded-md bg-muted/10 px-3 py-2 text-sm font-medium text-foreground transition hover:bg-muted/20"
                >
                  Search: {exploreSearchLabel}
                </Link>
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card p-5">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Score Distribution
              </h2>
              <div className="flex flex-col gap-2">
                {["4+", "3-4", "2-3", "1-2", "0-1"].map((label, i) => {
                  const idx = 4 - i;
                  const value = stats.scoreBuckets[idx];
                  const pct = Math.round((value / maxBucket) * 100);
                  return (
                    <div key={label} className="flex items-center gap-2">
                      <span className="w-8 text-right text-xs text-muted-foreground">
                        {label}
                      </span>
                      <div className="flex-1">
                        <div className="h-3 overflow-hidden rounded-full bg-muted/20">
                          <div
                            className={cn(
                              "h-full rounded-full transition-all",
                              idx >= 4
                                ? "bg-emerald-500"
                                : idx >= 3
                                  ? "bg-amber-500"
                                  : "bg-red-500",
                            )}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      <span className="w-6 text-right text-xs text-muted-foreground">
                        {value}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card p-5">
              <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Up Next
              </h2>
              {inboxJobs.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Inbox clear — no roles waiting for triage.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {inboxJobs.slice(0, 3).map((job) => (
                    <Link
                      key={job.id}
                      to={`/jobs/${job.id}`}
                      className="rounded-md border border-border px-3 py-2 transition hover:bg-muted/10"
                    >
                      <p className="text-sm font-medium text-foreground">
                        {job.company}
                      </p>
                      <p className="text-xs text-muted-foreground">{job.role}</p>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                Recent Activity
              </h2>
              <Link to="/jobs" className="text-sm text-brand hover:underline">
                View all
              </Link>
            </div>

            {recentJobs.length === 0 ? (
              <div className="rounded-lg border border-border bg-card p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  No jobs yet.{" "}
                  <Link to="/explore" className="text-brand hover:underline">
                    Find Jobs
                  </Link>{" "}
                  to get started.
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/20">
                      <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                        Company
                      </th>
                      <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                        Role
                      </th>
                      <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                        Status
                      </th>
                      <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                        Score
                      </th>
                      <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                        Added
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentJobs.map((job) => {
                      const pipeline = job.pipelineId
                        ? getPipelineForJob(job, pipelines)
                        : undefined;
                      const tone =
                        job.score !== undefined && job.score >= 0
                          ? scoreTone(String(job.score))
                          : "muted";
                      return (
                        <tr
                          key={job.id}
                          className="border-b border-border last:border-0 hover:bg-muted/10"
                        >
                          <td className="px-4 py-2.5 text-foreground">
                            {job.company}
                          </td>
                          <td className="px-4 py-2.5 text-foreground">
                            <Link
                              to={`/jobs/${job.id}`}
                              className="hover:text-brand"
                            >
                              {job.role}
                            </Link>
                          </td>
                          <td className="px-4 py-2.5 text-foreground">
                            {pipeline?.currentStage ?? "Pending"}
                          </td>
                          <td
                            className={cn(
                              "px-4 py-2.5 font-mono",
                              TONE_CLASS[tone],
                            )}
                          >
                            {job.score !== undefined ? `${job.score}/5` : "—"}
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground">
                            {new Date(job.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}

function StatCard({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border bg-card p-4 transition",
        highlight ? "border-brand/30 bg-brand/[0.03]" : "border-border",
      )}
    >
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}
